import React, { lazy } from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';
import { UnleashFeatureGuard } from 'guard/UnleashFeatureGuard';
import { useFlag } from 'utils/flags/hooks';
import { FEATURE_TOP_LEVEL_REPORTING } from 'utils/flags/constants';
import RouteAnalytics from './RouteAnalytics';

const ReportingEngine = lazy(() => import('containers/ReportingEngine'));
const TopLevelDashboardList = lazy(
  () => import('pages/app/reporting/dashboardList')
);
const TopLevelDashboard = lazy(() => import('pages/app/reporting/dashboard'));
const TopLevelWidgetList = lazy(() => import('pages/app/reporting/widgetList'));
const ReportingWidgetsList = lazy(
  () => import('containers/ReportingEngine/components/List/index')
);
const ReportingWidgetsStudio = lazy(
  () => import('containers/ReportingEngine/Widgets/WidgetStudio')
);
const TopLevelWidgetStudio = lazy(
  () => import('pages/app/reporting/widgetStudio')
);

// Governs the list routes and the singular studio route. Plural URLs are
// canonical when the flag is OFF, singular when ON; if the user is on the
// wrong form, redirect to the canonical one, preserving query string and
// hash. The plural-studio route uses `WidgetStudioRoute` below instead — it
// renders the new TLR stub when the flag is ON rather than redirecting.
const WidgetRouteGuard = ({
  children,
  pageName,
}: {
  children: React.ReactNode;
  pageName: string;
}) => {
  const isTopLevelReporting = useFlag(FEATURE_TOP_LEVEL_REPORTING);
  const location = useLocation();

  const isPlural = location.pathname.includes('/widgets/');

  if (isPlural === isTopLevelReporting) {
    const pathname = isPlural
      ? location.pathname.replace('/widgets/', '/widget/')
      : location.pathname.replace('/widget/', '/widgets/');
    return (
      <Navigate
        to={{ pathname, search: location.search, hash: location.hash }}
        replace
      />
    );
  }

  return <RouteAnalytics pageName={pageName}>{children}</RouteAnalytics>;
};

const WidgetStudioRoute = () => {
  const isTopLevelReporting = useFlag(FEATURE_TOP_LEVEL_REPORTING);

  if (isTopLevelReporting) {
    return (
      <RouteAnalytics pageName="TopLevelReporting/WidgetStudio">
        <TopLevelWidgetStudio />
      </RouteAnalytics>
    );
  }

  return (
    <RouteAnalytics pageName="Widgets Studio">
      <ReportingWidgetsStudio />
    </RouteAnalytics>
  );
};

export const reportingRoutes = () => (
  <Route path="reporting">
    <Route
      index
      path="dashboard"
      element={
        <RouteAnalytics pageName="Reporting/Dashboard">
          <ReportingEngine />
        </RouteAnalytics>
      }
    />

    <Route
      path="widgets/list"
      element={
        <WidgetRouteGuard pageName="Reporting/List">
          <ReportingWidgetsList />
        </WidgetRouteGuard>
      }
    />
    <Route path="widgets/:widgetId/studio" element={<WidgetStudioRoute />} />

    <Route
      path="widget/list"
      element={
        <WidgetRouteGuard pageName="Reporting/List">
          <ReportingWidgetsList />
        </WidgetRouteGuard>
      }
    />
    <Route
      path="widget/:widgetId/studio"
      element={
        <WidgetRouteGuard pageName="Widgets Studio">
          <ReportingWidgetsStudio />
        </WidgetRouteGuard>
      }
    />
    <Route
      path="widgets"
      element={
        <UnleashFeatureGuard featureFlag={FEATURE_TOP_LEVEL_REPORTING}>
          <RouteAnalytics pageName="TopLevelReporting/WidgetList">
            <TopLevelWidgetList />
          </RouteAnalytics>
        </UnleashFeatureGuard>
      }
    />
    <Route
      path="dashboards"
      element={
        <UnleashFeatureGuard featureFlag={FEATURE_TOP_LEVEL_REPORTING}>
          <RouteAnalytics pageName="TopLevelReporting/DashboardList">
            <TopLevelDashboardList />
          </RouteAnalytics>
        </UnleashFeatureGuard>
      }
    />
    <Route
      path="dashboards/:dashboardId"
      element={
        <UnleashFeatureGuard featureFlag={FEATURE_TOP_LEVEL_REPORTING}>
          <RouteAnalytics pageName="TopLevelReporting/Dashboard">
            <TopLevelDashboard />
          </RouteAnalytics>
        </UnleashFeatureGuard>
      }
    />
  </Route>
);
